import {
  StatsigClient,
  StatsigProvider as StatsigProviderBindings,
} from '@statsig/react-bindings';
import { useMemo, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

import type { StatsigProvider_query$key } from '@/providers/__generated__/StatsigProvider_query.graphql';

type StatsigProviderProps = {
  children: ReactNode;
  query: StatsigProvider_query$key;
};

const StatsigProvider = ({ children, query }: StatsigProviderProps) => {
  const data = useFragment(
    graphql`
      fragment StatsigProvider_query on Query {
        statsigInitializeResponse {
          userID
          customIDs {
            key
            value
          }
          initialValues
          privateAttributes
        }
      }
    `,
    query,
  );
  const response = data.statsigInitializeResponse;

  const client = useMemo(() => {
    const statsigClient = new StatsigClient(
      import.meta.env.VITE_STATSIG_API_KEY,
      {
        userID: response?.userID ?? undefined,
        customIDs:
          response?.customIDs?.reduce((acc, curr) => {
            if (curr.key && curr.value) {
              return { ...acc, [curr.key]: curr.value };
            }
            return acc;
          }, {}) ?? undefined,
        privateAttributes: response?.privateAttributes ?? undefined,
      },
      {
        environment: {
          tier: import.meta.env.VITE_STATSIG_ENVIRONMENT,
        },
        networkConfig: {
          api: 'https://f.getbudgy.co.uk/v1',
        },
        logLevel: 0,
      },
    );
    statsigClient.dataAdapter.setData(response?.initialValues ?? '');
    statsigClient.initializeSync();
    return statsigClient;
  }, [
    response?.userID,
    response?.customIDs,
    response?.privateAttributes,
    response?.initialValues,
  ]);

  return (
    <StatsigProviderBindings key={response?.userID} client={client}>
      {children}
    </StatsigProviderBindings>
  );
};

export default StatsigProvider;
